import { fetchSection, validateViewPosition } from "src/utils";

class FilterInlineList extends HTMLElement {
  private items!: NodeListOf<HTMLElement>;
  private popup: HTMLElement | null = null;
  private popupTitle: HTMLElement | null = null;
  private popupContent: HTMLElement | null = null;
  private activeItem: HTMLElement | null = null;
  private events: (() => void)[] = [];
  private activeFiltersHandler: ((e: Event) => void) | null = null;

  connectedCallback(): void {
    this.items = this.querySelectorAll(".filter-inline-list > li");
    this.popup = this.querySelector("[data-popup]");
    this.popupTitle = this.querySelector("[data-popup-title]");
    this.popupContent = this.querySelector("[data-popup-content]");

    this.init();
    this.updateActiveCounts();
    this.prepareActiveFilters();
    this.listenForDrawerChanges();
  }

  disconnectedCallback(): void {
    this.unload();
  }

  private init(): void {
    this.items.forEach((item) => {
      const button = item.querySelector<HTMLButtonElement>("button");
      if (!button) return;

      const clickHandler = (e: Event) => {
        e.stopPropagation();
        this.togglePopup(item);
      };
      button.addEventListener("click", clickHandler);
      this.events.push(() => button.removeEventListener("click", clickHandler));
    });

    // Close popup when clicking outside
    const documentClickHandler = (e: Event) => {
      if (this.popup && !this.popup.contains(e.target as Node)) {
        const clickedItem = Array.from(this.items).find((item) => {
          const button = item.querySelector("button");
          return button && button.contains(e.target as Node);
        });
        if (!clickedItem) {
          this.closePopup();
        }
      }
    };
    document.addEventListener("click", documentClickHandler);
    this.events.push(() =>
      document.removeEventListener("click", documentClickHandler),
    );

    const keydownHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && this.popup?.classList.contains("is-active")) {
        this.closePopup();
      }
    };
    document.addEventListener("keydown", keydownHandler);
    this.events.push(() =>
      document.removeEventListener("keydown", keydownHandler),
    );

    this.handleButtons();
    this.handleScroll();
  }

  private handleButtons() {
    const applyButton = this.querySelector("[data-popup-apply]");
    if (applyButton) {
      const clickHandler = (e: Event) => {
        e.stopPropagation();
        this.syncCheckboxesToTemplates();
        this.updateFilters();
        this.closePopup();
      };
      applyButton.addEventListener("click", clickHandler);
      this.events.push(() =>
        applyButton.removeEventListener("click", clickHandler),
      );
    }

    const clearButton = this.querySelector("[data-popup-clear]");
    if (clearButton) {
      const clickHandler = (e: Event) => {
        e.stopPropagation();
        if (this.popupContent) {
          const checkboxes =
            this.popupContent.querySelectorAll<HTMLInputElement>(
              ".filter-item__checkbox",
            );
          checkboxes.forEach((checkbox) => {
            checkbox.checked = false;
          });
          this.updateActiveCountForCurrentItem();
        }
      };
      clearButton.addEventListener("click", clickHandler);
      this.events.push(() =>
        clearButton.removeEventListener("click", clickHandler),
      );
    }
  }

  private prepareActiveFilters() {
    if (this.activeFiltersHandler) return;

    this.activeFiltersHandler = (e: Event) => {
      const target = (e.target as HTMLElement).closest(
        ".active-filters > a",
      ) as HTMLAnchorElement | null;
      if (!target) return;

      e.preventDefault();
      e.stopPropagation();

      const href = target.href;
      const params = new URL(href).searchParams;

      // Update template checkboxes to match href params (unchecks the removed filter)
      this.updateCheckboxes(".filter-item__checkbox", (checkbox) => {
        const values = params.getAll(checkbox.name);
        checkbox.checked = values.includes(checkbox.value);
      });

      this.updateFilters();
    };
    document.addEventListener("click", this.activeFiltersHandler);
    this.events.push(() => {
      if (this.activeFiltersHandler) {
        document.removeEventListener("click", this.activeFiltersHandler);
      }
    });
  }

  private handleScroll() {
    const scrollContainer = this.querySelector(".filter-inline-list");
    if (!scrollContainer) return;

    let rafId: number | null = null;

    const scrollHandler = () => {
      if (rafId !== null) return;

      rafId = requestAnimationFrame(() => {
        rafId = null;

        if (this.popup?.classList.contains("is-active") && this.activeItem) {
          const button = this.activeItem.querySelector("button");
          if (!button) return;

          const itemRect = button.getBoundingClientRect();
          const containerRect = scrollContainer.getBoundingClientRect();

          if (
            itemRect.right < containerRect.left ||
            itemRect.left > containerRect.right
          ) {
            this.closePopup();
            return;
          }

          validateViewPosition(this.popup, button, this as HTMLElement);
        }
      });
    };
    scrollContainer.addEventListener("scroll", scrollHandler);
    this.events.push(() => {
      scrollContainer.removeEventListener("scroll", scrollHandler);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    });
  }

  private togglePopup(item: HTMLElement): void {
    if (this.activeItem === item) {
      this.closePopup();
    } else {
      this.openPopup(item);
    }
  }

  private openPopup(item: HTMLElement): void {
    if (this.activeItem) {
      this.syncCheckboxesToTemplates();
      this.activeItem.classList.remove("is-active");
      const prevButton = this.activeItem.querySelector("button");
      if (prevButton) {
        prevButton.setAttribute("aria-expanded", "false");
      }
    }

    this.activeItem = item;
    item.classList.add("is-active");
    const button = item.querySelector("button");
    if (button) {
      button.setAttribute("aria-expanded", "true");
    }

    const label = item.dataset.filterLabel || "";
    const template = item.querySelector<HTMLTemplateElement>(
      "[data-filter-values]",
    );

    if (this.popupTitle) {
      this.popupTitle.textContent = label;
    }

    if (this.popupContent && template) {
      this.popupContent.innerHTML = "";
      const content = template.content.cloneNode(true);
      this.popupContent.appendChild(content);

      const checkboxes = this.popupContent.querySelectorAll(
        ".filter-item__checkbox",
      );
      checkboxes.forEach((checkbox) => {
        checkbox.addEventListener("change", () => {
          this.updateActiveCountForCurrentItem();
        });
      });
    }

    if (!this.popup) {
      return;
    }

    this.popup.classList.add("is-active");
    this.popup.setAttribute("aria-hidden", "false");

    const itemButton = item.querySelector("button");
    if (itemButton) {
      validateViewPosition(this.popup, itemButton, this as HTMLElement);
    }
  }

  private closePopup(): void {
    this.syncCheckboxesToTemplates();

    if (this.popup) {
      this.popup.classList.remove("is-active");
      this.popup.setAttribute("aria-hidden", "true");
    }

    if (this.activeItem) {
      this.activeItem.classList.remove("is-active");
      const button = this.activeItem.querySelector("button");
      if (button) {
        button.setAttribute("aria-expanded", "false");
      }
      this.activeItem = null;
    }
  }

  private syncCheckboxesToTemplates(): void {
    if (!this.activeItem || !this.popupContent) return;

    const template = this.activeItem.querySelector<HTMLTemplateElement>(
      "[data-filter-values]",
    );
    if (!template) return;

    // Get current checkbox states from popup
    const popupCheckboxes =
      this.popupContent.querySelectorAll<HTMLInputElement>(
        ".filter-item__checkbox",
      );

    // Create a map of checkbox states
    const checkboxStates = new Map<string, boolean>();
    popupCheckboxes.forEach((checkbox) => {
      checkboxStates.set(checkbox.id, checkbox.checked);
    });

    // Update template content with new states
    const templateContent = template.content;
    const templateCheckboxes =
      templateContent.querySelectorAll<HTMLInputElement>(
        ".filter-item__checkbox",
      );
    templateCheckboxes.forEach((checkbox) => {
      const state = checkboxStates.get(checkbox.id);
      if (state !== undefined) {
        checkbox.checked = state;
      }
    });
  }

  private async updateFilters() {
    const filterDrawerForm = document.querySelector(
      "[data-filter-form]",
    ) as HTMLFormElement;

    if (!filterDrawerForm) {
      this.navigateWithFilters();
      return;
    }

    this.syncToFilterDrawer(filterDrawerForm);

    const applyButton = filterDrawerForm.querySelector(
      '[data-button="apply"]',
    ) as HTMLButtonElement;
    if (applyButton) {
      applyButton.click();
    }

  }

  private syncToFilterDrawer(form: HTMLFormElement): void {
    this.updateCheckboxes(".filter-item__checkbox", (checkbox) => {
      const escapedValue = CSS.escape(checkbox.value);
      const formCheckbox = form.querySelector<HTMLInputElement>(
        `input[name="${checkbox.name}"][value="${escapedValue}"]`,
      );
      if (formCheckbox) {
        formCheckbox.checked = checkbox.checked;
      }
    });

    this.syncDrawerActiveStates(form);
  }

  private syncDrawerActiveStates(form: HTMLFormElement): void {
    const groups = form.querySelectorAll<HTMLElement>(
      ".filter-drawer__group",
    );
    groups.forEach((group) => {
      const hasChecked = group.querySelector<HTMLInputElement>(
        "input:checked",
      );
      group.classList.toggle("filters-active", !!hasChecked);
    });
  }

  private listenForDrawerChanges(): void {
    const handler = () => {
      this.syncFromFilterDrawer();
      this.fetchUpdatedFilterCounts().then(() => {
        this.updateActiveCounts();
      });
    };
    document.addEventListener("products:loaded", handler);
    this.events.push(() =>
      document.removeEventListener("products:loaded", handler),
    );
  }

  private syncFromFilterDrawer(): void {
    const form = document.querySelector(
      "[data-filter-form]",
    ) as HTMLFormElement;
    if (!form) return;

    this.updateCheckboxes(".filter-item__checkbox", (checkbox) => {
      const escapedValue = CSS.escape(checkbox.value);
      const formCheckbox = form.querySelector<HTMLInputElement>(
        `input[name="${checkbox.name}"][value="${escapedValue}"]`,
      );
      if (formCheckbox) {
        checkbox.checked = formCheckbox.checked;
      }
    });

    this.updateActiveCounts();
  }

  private navigateWithFilters(): void {
    const params = new URLSearchParams(window.location.search);

    this.updateCheckboxes(".filter-item__checkbox", (checkbox) => {
      params.delete(checkbox.name);
    });

    this.updateCheckboxes(".filter-item__checkbox:checked", (checkbox) => {
      params.append(checkbox.name, checkbox.value);
    });

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.location.href = newUrl;
  }

  private buildFilterSearchParams() {
    const params = new URLSearchParams(window.location.search);

    this.updateCheckboxes(".filter-item__checkbox", (checkbox) => {
      params.delete(checkbox.name);
    });

    this.updateCheckboxes(".filter-item__checkbox:checked", (checkbox) => {
      params.append(checkbox.name, checkbox.value);
    });

    return params;
  }

  private async fetchUpdatedFilterCounts() {
    const searchParams = this.buildFilterSearchParams();

    try {
      const sectionDoc = await fetchSection(this, searchParams);
      const newFilterList = sectionDoc.querySelector("filter-inline-list");
      if (!newFilterList) return;

      const newItems = newFilterList.querySelectorAll(
        ".filter-inline-list > li",
      );

      newItems.forEach((newItem) => {
        const filterKey = (newItem as HTMLElement).dataset.filterItem;
        if (!filterKey) return;

        const currentItem = this.querySelector(
          `[data-filter-item="${filterKey}"]`,
        );
        if (!currentItem) return;

        const currentTemplate = currentItem.querySelector<HTMLTemplateElement>(
          "[data-filter-values]",
        );
        const newTemplate = newItem.querySelector<HTMLTemplateElement>(
          "[data-filter-values]",
        );
        if (!currentTemplate || !newTemplate) return;

        // Preserve checked states from current template
        const checkedValues = new Set<string>();
        currentTemplate.content
          .querySelectorAll<HTMLInputElement>(".filter-item__checkbox:checked")
          .forEach((cb) => checkedValues.add(cb.value));

        // Replace template content with new HTML (updated counts + disabled states)
        currentTemplate.content.replaceChildren(
          ...Array.from(newTemplate.content.childNodes).map((n) =>
            n.cloneNode(true),
          ),
        );

        // Restore checked states
        currentTemplate.content
          .querySelectorAll<HTMLInputElement>(".filter-item__checkbox")
          .forEach((cb) => {
            cb.checked = checkedValues.has(cb.value);
          });
      });

      this.updateFilterDrawerCounts(sectionDoc);
    } catch {
      // Silently fail — counts will remain stale until next page load
    }
  }

  private updateFilterDrawerCounts(sectionDoc: Document): void {
    const drawerForm = document.querySelector("[data-filter-form]");
    if (!drawerForm) return;

    const newDrawer = sectionDoc.querySelector("[data-filter-form]");
    if (!newDrawer) return;

    const newFilterItems =
      newDrawer.querySelectorAll<HTMLElement>(".filter-item");
    newFilterItems.forEach((newItem) => {
      const checkbox = newItem.querySelector<HTMLInputElement>(
        ".filter-item__checkbox",
      );
      if (!checkbox) return;

      const escapedValue = CSS.escape(checkbox.value);
      const currentCheckbox = drawerForm.querySelector<HTMLInputElement>(
        `input[name="${checkbox.name}"][value="${escapedValue}"]`,
      );
      if (!currentCheckbox) return;

      const currentItem = currentCheckbox.closest(".filter-item");
      if (!currentItem) return;

      // Update product count
      const newCount = newItem.querySelector(".filter-item__count");
      const currentCount = currentItem.querySelector(".filter-item__count");
      if (newCount && currentCount) {
        currentCount.textContent = newCount.textContent;
      }

      // Update disabled state
      const newContent = newItem.querySelector(".filter-item__content");
      const currentContent = currentItem.querySelector(".filter-item__content");
      if (newContent && currentContent) {
        currentContent.classList.toggle(
          "filter-item__content--disabled",
          newContent.classList.contains("filter-item__content--disabled"),
        );
      }
      currentCheckbox.disabled = checkbox.disabled;
    });
  }

  private updateCheckboxes(
    className: string,
    callback: (checkbox: HTMLInputElement) => void,
  ) {
    this.items.forEach((item) => {
      const template = item.querySelector<HTMLTemplateElement>(
        "[data-filter-values]",
      );
      if (!template) return;

      const checkboxes =
        template.content.querySelectorAll<HTMLInputElement>(className);

      checkboxes.forEach((checkbox) => {
        callback(checkbox);
      });
    });
  }

  private updateActiveCountForCurrentItem(): void {
    if (!this.activeItem || !this.popupContent) return;

    this.updateCountForItem(this.popupContent, this.activeItem);
  }

  private updateActiveCounts(): void {
    this.items.forEach((item) => {
      const template = item.querySelector<HTMLTemplateElement>(
        "[data-filter-values]",
      );
      if (!template) return;

      this.updateCountForItem(template.content, item);
    });
  }

  private updateCountForItem(
    templateContent: ParentNode | null,
    item: HTMLElement | null,
  ): void {
    if (!templateContent || !item) return;

    const checkboxes = templateContent.querySelectorAll<HTMLInputElement>(
      ".filter-item__checkbox:checked",
    );
    const count = checkboxes.length;

    let countElement = item.querySelector(".filter-inline-list__item-count");

    if (count > 0) {
      if (!countElement) {
        countElement = document.createElement("span");
        countElement.className = "filter-inline-list__item-count";
        const label = item.querySelector(".filter-inline-list__item-label");
        if (label) {
          label.appendChild(countElement);
        }
      }
      countElement.textContent = `(${count})`;
    } else if (countElement) {
      countElement.remove();
    }
  }

  private unload(): void {
    this.events.forEach((unsubscribe) => unsubscribe());
    this.closePopup();
  }
}

// Register custom element
if (!customElements.get("filter-inline-list")) {
  customElements.define("filter-inline-list", FilterInlineList);
}

export default FilterInlineList;
